import { twoline2satrec, propagate, type SatRec } from "satellite.js";

/* ------------------------------------------------------------------ *
 * Building a TLE (two-line element set) from orbital values -- the
 * satellite's first TLE after its launch (see ascent.ts). Column layout
 * and number formats follow the standard NORAD/Celestrak TLE format.
 * ------------------------------------------------------------------ */

export const EARTH_RADIUS_KM = 6378.137;
const MU_KM3_S2 = 398600.4418; // Earth's gravitational parameter
const J2 = 1.08262668e-3; // Earth's oblateness
const SECONDS_PER_DAY = 86400;

export type TleInput = {
  catalogNumber: number; // 1..99999
  launchYear: number; // full year, e.g. 2026
  launchNumber: number; // 1..999
  launchPiece: string; // "A".."ZZZ"
  epoch: Date;
  bstar: number; // drag term, 1/earth radii
  meanMotionDot?: number; // change of the mean motion (rev/day²), shown halved in the TLE as is customary
  elementSetNumber: number; // 1..9999
  inclinationDeg: number; // 0..180
  raanDeg: number; // 0..360
  eccentricity: number; // 0..<1
  argPerigeeDeg: number; // 0..360
  meanAnomalyDeg: number; // 0..360
  meanAltitudeKm: number; // semi-major axis minus Earth's radius
  revolutionNumber: number; // 0..99999
};

// Revolutions per day for an orbit whose semi-major axis is Earth's radius
// plus the given altitude (Kepler's third law).
export function meanMotionRevPerDay(meanAltitudeKm: number): number {
  const a = EARTH_RADIUS_KM + meanAltitudeKm;
  const radPerSecond = Math.sqrt(MU_KM3_S2 / (a * a * a));
  return (radPerSecond * SECONDS_PER_DAY) / (2 * Math.PI);
}

export function orbitalPeriodMinutes(meanAltitudeKm: number): number {
  return (24 * 60) / meanMotionRevPerDay(meanAltitudeKm);
}

// The inclination at which Earth's oblateness turns the orbit plane by
// exactly one turn a year, so the satellite always passes at the same local
// time: a sun-synchronous orbit. Null when no such inclination exists (the
// orbit is too high).
export function sunSynchronousInclinationDeg(meanAltitudeKm: number, eccentricity = 0): number | null {
  const a = EARTH_RADIUS_KM + meanAltitudeKm;
  const precessionRadPerSecond = (2 * Math.PI) / (365.2421897 * SECONDS_PER_DAY);
  const cosI =
    -(2 * precessionRadPerSecond * Math.pow(a, 3.5) * Math.pow(1 - eccentricity * eccentricity, 2)) /
    (3 * J2 * EARTH_RADIUS_KM * EARTH_RADIUS_KM * Math.sqrt(MU_KM3_S2));
  if (cosI < -1 || cosI > 1) return null;
  return (Math.acos(cosI) * 180) / Math.PI;
}

// Last digit of the sum of all digits, with each minus sign counting as 1.
export function tleChecksum(line68: string): number {
  let sum = 0;
  for (const char of line68) {
    if (char >= "0" && char <= "9") sum += Number(char);
    else if (char === "-") sum += 1;
  }
  return sum % 10;
}

const fixed = (value: number, width: number, decimals: number) => value.toFixed(decimals).padStart(width, " ");

// " .00012345" / "-.00012345": a value below 1 without its leading zero.
function decimalPointAssumed(value: number): string {
  const sign = value < 0 ? "-" : " ";
  return sign + Math.abs(value).toFixed(8).slice(1);
}

// TLE "exponential" notation, 8 characters: " 12345-3" means 0.12345e-3.
function tleExponent(value: number): string {
  if (value === 0) return " 00000-0";
  const sign = value < 0 ? "-" : " ";
  let exponent = Math.floor(Math.log10(Math.abs(value))) + 1;
  let mantissa = Math.round((Math.abs(value) / Math.pow(10, exponent)) * 1e5);
  if (mantissa >= 1e5) {
    mantissa = Math.round(mantissa / 10);
    exponent += 1;
  }
  const expSign = exponent < 0 ? "-" : "+";
  return sign + String(mantissa).padStart(5, "0") + expSign + String(Math.abs(exponent));
}

// Epoch as YYDDD.DDDDDDDD: two-digit year, then day of the year (1 January =
// day 1) with the time of day as a fraction.
function tleEpoch(epoch: Date): { year: string; day: string } {
  const startOfYear = Date.UTC(epoch.getUTCFullYear(), 0, 1);
  const day = (epoch.getTime() - startOfYear) / (SECONDS_PER_DAY * 1000) + 1;
  return {
    year: String(epoch.getUTCFullYear() % 100).padStart(2, "0"),
    day: day.toFixed(8).padStart(12, "0"),
  };
}

const normalizeDeg = (deg: number) => ((deg % 360) + 360) % 360;

export function buildTle(input: TleInput): { line1: string; line2: string } {
  const catalog = String(Math.trunc(input.catalogNumber)).padStart(5, "0");
  const epoch = tleEpoch(input.epoch);
  const designator =
    String(input.launchYear % 100).padStart(2, "0") +
    String(Math.trunc(input.launchNumber)).padStart(3, "0") +
    input.launchPiece.toUpperCase().padEnd(3, " ");

  const body1 =
    `1 ${catalog}U ${designator} ${epoch.year}${epoch.day} ${decimalPointAssumed(input.meanMotionDot ?? 0)} ${tleExponent(0)} ` +
    `${tleExponent(input.bstar)} 0 ${String(Math.trunc(input.elementSetNumber)).padStart(4, " ")}`;

  const eccentricity = String(Math.round(input.eccentricity * 1e7)).padStart(7, "0");
  const body2 =
    `2 ${catalog} ${fixed(input.inclinationDeg, 8, 4)} ${fixed(normalizeDeg(input.raanDeg), 8, 4)} ${eccentricity} ` +
    `${fixed(normalizeDeg(input.argPerigeeDeg), 8, 4)} ${fixed(normalizeDeg(input.meanAnomalyDeg), 8, 4)} ` +
    `${fixed(meanMotionRevPerDay(input.meanAltitudeKm), 11, 8)}${String(Math.trunc(input.revolutionNumber)).padStart(5, " ")}`;

  return { line1: body1 + tleChecksum(body1), line2: body2 + tleChecksum(body2) };
}

// Parses a TLE with the same model the pass prediction uses. Null when it
// can't be propagated.
export function parseTle(line1: string, line2: string): SatRec | null {
  try {
    const satrec = twoline2satrec(line1, line2);
    if (satrec.error !== 0) return null;
    const state = propagate(satrec, new Date());
    return state && typeof state.position === "object" ? satrec : null;
  } catch {
    return null;
  }
}
